import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import api from '../../utils/api';
import { X, Building2, Calendar, User, ExternalLink } from 'lucide-react';
import { getJobRowStyle, JOB_STATUS_LABELS } from '../../utils/jobStatusColor';
import { ISSUE_LABELS, issueManuallyPath } from '../../constants/jobModel';
import { applyInspectionDue } from '../../utils/inspectionDueDate';
import ForceMoveControl from '../../components/ForceMoveControl';

const PROGRESS_STEPS = ['PENDING', 'QUERIED', 'AUCTION', 'ACCEPTED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED'];

function formatDate(value) {
  if (!value) return '—';
  try {
    return format(parseISO(String(value).slice(0, 10)), 'd MMM yyyy');
  } catch {
    return String(value).slice(0, 10);
  }
}

export default function JobScheduleModal({ event, onClose }) {
  const navigate = useNavigate();
  const hasJob = Boolean(event?.job_id);

  const { data: jobDetail, isLoading } = useQuery({
    queryKey: ['job', event?.job_id, 'schedule-modal'],
    queryFn: async () => {
      const response = await api.get(`/jobs/${event.job_id}`);
      return response.data;
    },
    enabled: hasJob,
  });

  const job = jobDetail;
  const building = jobDetail?.building;
  const status = job?.status || event?.status || 'Due soon';
  const daysUntil = event?.days_until_due ?? null;
  const style = getJobRowStyle(status, daysUntil, !hasJob);

  const activeStepIndex = (() => {
    if (status === 'QUERIED' || status === 'AUCTION') return PROGRESS_STEPS.indexOf('QUERIED');
    const idx = PROGRESS_STEPS.indexOf(status);
    return idx >= 0 ? idx : 0;
  })();

  const openJob = () => {
    if (event.job_id) {
      navigate(`/jobs/${event.job_id}`);
      onClose();
    }
  };

  const openWorkForm = () => {
    if (event.building_id) {
      navigate(issueManuallyPath(event.building_id));
      onClose();
    }
  };

  const openBuildingProgress = () => {
    if (event.building_id) {
      navigate(`/buildings/${event.building_id}#ss-progress`);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-card rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {event.building_name || 'Building'}
              {(event.ss_code || event.ss_name) && (
                <span className="text-gray-500 font-normal"> · {event.ss_name || event.ss_code}</span>
              )}
            </h3>
            {event.job_id && <p className="text-sm text-gray-500 mt-1">Inspection #{event.job_id}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded ${style.badge}`}>
              {JOB_STATUS_LABELS[status] || status}
            </span>
            {daysUntil != null && (
              <span className={`text-sm ${style.date}`}>
                {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : `${daysUntil} days until due`}
              </span>
            )}
          </div>

          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <dt className="text-gray-500">Building</dt>
                <dd>{event.building_name || building?.name || '—'}</dd>
                {(event.building_address || building?.address) && (
                  <dd className="text-gray-500 text-xs">{event.building_address || building?.address}</dd>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <dt className="text-gray-500">Due date</dt>
                <dd>{formatDate(job ? applyInspectionDue(job).due_date : event.due_date)}</dd>
                {(job?.scheduled_date || event.scheduled_date) && (
                  <>
                    <dt className="text-gray-500 mt-1">Scheduled</dt>
                    <dd>{formatDate(job?.scheduled_date || event.scheduled_date)}</dd>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <dt className="text-gray-500">Assigned worker</dt>
                <dd>
                  {jobDetail?.assigned_worker_name ||
                    event.assigned_worker_name ||
                    job?.assigned_worker_id ||
                    'Not assigned'}
                </dd>
              </div>
            </div>
          </dl>

          {(job?.scheduled_date || event?.scheduled_date) && (
            <div
              className={`rounded-lg border p-3 space-y-1 ${
                job?.schedule_ack_at || event?.schedule_ack_at
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-violet-200 bg-violet-50'
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  job?.schedule_ack_at || event?.schedule_ack_at ? 'text-blue-800' : 'text-violet-900'
                }`}
              >
                {job?.schedule_ack_at || event?.schedule_ack_at
                  ? 'Worker confirmed schedule'
                  : 'Awaiting worker confirm'}
              </p>
              {(job?.schedule_ack_at || event?.schedule_ack_at) && (
                <p className="text-sm text-blue-950">
                  Confirmed{' '}
                  {String(job?.schedule_ack_at || event?.schedule_ack_at)
                    .replace('T', ' ')
                    .slice(0, 16)}
                </p>
              )}
              {(job?.schedule_ack_note || event?.schedule_ack_note) &&
                String(job?.schedule_ack_note || event?.schedule_ack_note) !== 'legacy-auto' &&
                String(job?.schedule_ack_note || event?.schedule_ack_note) !==
                  'Worker set visit date' && (
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">
                    <span className="text-gray-500">Decision / note: </span>
                    {job?.schedule_ack_note || event?.schedule_ack_note}
                  </p>
                )}
              {!(job?.schedule_ack_at || event?.schedule_ack_at) && (
                <p className="text-xs text-amber-900">
                  Yellow on the calendar until the worker opens this schedule and taps Confirm.
                </p>
              )}
            </div>
          )}

          {hasJob && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Progress</p>
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading inspection details…</p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {['Pending', 'Query', 'Accepted', 'Scheduled', 'In progress', 'Done'].map((label, idx) => {
                    const stepIdx = [0, 1, 3, 4, 5, 6][idx];
                    const done = activeStepIndex >= stepIdx;
                    const current = activeStepIndex === stepIdx || (activeStepIndex === 1 && idx === 1);
                    return (
                      <span
                        key={label}
                        className={`text-xs px-2 py-1 rounded ${
                          current
                            ? 'bg-primary-600 text-white font-medium'
                            : done
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!hasJob && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              No inspection pack yet. Wait for due to ask the IQP for a date. Use{' '}
              <strong>{ISSUE_LABELS.verb}</strong> only if that pack is missing.
            </p>
          )}

          {hasJob && (
            <ForceMoveControl
              jobId={event.job_id}
              buildingId={event.building_id || building?.id}
              dueDate={job?.due_date || event.due_date}
              scheduledDate={job?.scheduled_date || event.scheduled_date}
              buildingName={event.building_name || building?.name}
              ssCode={event.ss_code || job?.ss_code}
              ssName={event.ss_name || job?.ss_name}
              days={daysUntil}
              currentStatus={job?.status || event?.status}
              onMoved={onClose}
            />
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex flex-wrap gap-2">
          {hasJob ? (
            <button
              type="button"
              onClick={openJob}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open inspection
            </button>
          ) : (
            <button
              type="button"
              onClick={openWorkForm}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
            >
              {ISSUE_LABELS.verb}
            </button>
          )}
          <button
            type="button"
            onClick={openBuildingProgress}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-sm"
          >
            Building SS progress
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm ml-auto">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
